import { Injectable } from '@angular/core';
import { formatAmount, formatNumber, normalizeAmount, parseAmount } from '../../../utils/format-amount';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { Store, UpdateFn } from '../../../services/store';
import { Point } from '../../../models/point';
import { toVoid } from '../../../utils/void';
import { groupBy } from '../../../utils/group-by';
import { ProductionOverseerProvider } from './production-overseer.provider';
import { OperationType } from './models/operation-type.model';
import { ProgressAllocation, ReportKind, WorkLine } from './models/work-line.model';
import { ContextTreeVm, ItemGroupVm, OrderGroupVm, OverseerSummary, OverseerViewModel, StageGroupVm, StageLineVm, StageRowFocus } from './models/overseer.view-model';

export type OverseerViewMode = 'byItem' | 'byOrder' | 'byOperation';

export type OverseerWorkFilter = 'ready' | 'all';

// Where the open report form sits (a group or a line), which column it reports into
// (null = the untagged Zgłoś form), and what the row offered when it was opened.
export type ReportTarget = ReportRow & ReportForm;

// Under Wg etapów a group is an item under its parent, so bomItemId alone does not name it
export type ReportRow =
  | { kind: 'item'; bomItemId: number }
  | { kind: 'stageGroup'; groupKey: string }
  | { kind: 'line'; lineId: number };

export interface ReportForm {
  operationTypeId: number | null;
  completesItem: boolean;
  prefill: number; // 0 renders as an empty input; no max — a text input (0111), allocate caps at the remaining
  // null = the form in its row; a point = the floating panel a DRZEWO node's menu opens (0102)
  openAt: Point | null;
}

// The one inline name input open in the header: a new column at a slot (position = where it
// lands, operationTypes.length = append) or an existing column being renamed
export type HeaderEdit =
  | { kind: 'insert'; position: number }
  | { kind: 'rename'; id: number };

export interface HeaderMenu {
  operationTypeId: number;
  position: Point;
}

// The right-click menu on a DRZEWO node (0102): the line it acts on, at the selected stage
export interface TreeMenu {
  lineId: number;
  position: Point;
}

// The last action from the view, kept for its confirmation and Cofnij: amounts reported,
// or a stage decision (Nic do zrobienia; skips = false is Przywróć). Both are one report
// on the API side, so undo deletes the same way.
export type LastReport = LastReportRow & (
  | { action: 'report'; allocations: ProgressAllocation[]; kind: ReportKind } // undo mirrors the same columns
  // learnedLineIds: the lines that skipped the stage from the part's memory before a Przywróć —
  // undo gives them that back
  | { action: 'skip'; lineIds: number[]; operationTypeId: number; skips: boolean; learnedLineIds: number[] });

export interface LastReportRow {
  reportId: number; // server-side id, used to undo
  bomItemId: number;
  groupKey: string; // the same row under Wg etapów
  message: string;
}

export interface ProductionOverseerState {
  loading: boolean;
  viewMode: OverseerViewMode;
  workFilter: OverseerWorkFilter;
  lines: WorkLine[];
  operationTypes: OperationType[]; // dictionary order = column order
  selectedOperationTypeId: number | null; // Wg etapów; null (or an entry since deleted) = the first one
  expandedItems: number[];
  expandedStageGroups: string[];
  focusedStageRow: StageRowFocus | null; // Wg etapów: the row whose zlecenie tree the DRZEWO card shows (0098)
  reportTarget: ReportTarget | null;
  lastReport: LastReport | null;
  headerEdit: HeaderEdit | null;
  headerEditError: string | null;
  headerMenu: HeaderMenu | null;
  treeMenu: TreeMenu | null;
  itemGroups: ItemGroupVm[];
  orderGroups: OrderGroupVm[];
  stageGroups: StageGroupVm[]; // rows of the selected stage
  stageCounts: Record<number, number>; // per operation: rows with something waiting there
  contextTree: ContextTreeVm | null; // the focused row's zlecenie tree at the selected stage
  summary: OverseerSummary;
}

// Wszystko by default (0108): the complete list is readable in tree order, its muted rows say
// what is not actionable, and the sheet stops jumping between two row sets with every report
const INITIAL_STATE: ProductionOverseerState = {
  loading: false,
  viewMode: 'byOperation',
  workFilter: 'all',
  lines: [],
  operationTypes: [],
  selectedOperationTypeId: null,
  expandedItems: [],
  expandedStageGroups: [],
  focusedStageRow: null,
  reportTarget: null,
  lastReport: null,
  headerEdit: null,
  headerEditError: null,
  headerMenu: null,
  treeMenu: null,
  itemGroups: [],
  orderGroups: [],
  stageGroups: [],
  stageCounts: {},
  contextTree: null,
  summary: { ordersInProgress: 0, ordersOverdue: 0, ordersDueThisWeek: 0 }
};

@Injectable({ providedIn: 'root' })
export class ProductionOverseerStore extends Store<ProductionOverseerState> {
  constructor(private provider: ProductionOverseerProvider) {
    super(INITIAL_STATE);
  }

  init(): Observable<void> {
    this.update(state => ({ ...state, loading: true }));

    return forkJoin([this.provider.getWorkLines(), this.provider.getOperationTypes()])
      .pipe(
        tap(([lines, operationTypes]) => {
          this.update(
            state => ({ ...state, loading: false, lines, operationTypes }),
            deriveViews
          );
        }),
        toVoid()
      );
  }

  setViewMode(viewMode: OverseerViewMode): void {
    this.update(state => ({ ...state, viewMode, reportTarget: null }));
  }

  setWorkFilter(workFilter: OverseerWorkFilter): void {
    this.update(
      state => ({ ...state, workFilter, reportTarget: null }),
      deriveViews
    );
  }

  selectOperationType(operationTypeId: number): void {
    this.update(
      state => ({ ...state, selectedOperationTypeId: operationTypeId, reportTarget: null }),
      deriveViews
    );
  }

  toggleItem(bomItemId: number): void {
    this.update(state => ({
      ...state,
      expandedItems: state.expandedItems.includes(bomItemId)
        ? state.expandedItems.filter(id => id !== bomItemId)
        : [...state.expandedItems, bomItemId]
    }));
  }

  // A click on a group row expands it and makes it the DRZEWO card's row — one gesture (0098)
  toggleStageGroup(key: string): void {
    this.update(
      state => ({
        ...state,
        expandedStageGroups: state.expandedStageGroups.includes(key)
          ? state.expandedStageGroups.filter(expanded => expanded !== key)
          : [...state.expandedStageGroups, key],
        focusedStageRow: { groupKey: key, lineId: null }
      }),
      deriveViews
    );
  }

  // A line row's click and every row action focus too, so the card shows what is being reported on
  focusStageRow(focus: StageRowFocus): void {
    this.update(state => ({ ...state, focusedStageRow: focus }), deriveViews);
  }

  // A DRZEWO node's click (0100): the clicked line's row takes the focus and is made visible —
  // under Wszystko when Do zrobienia does not list it, expanded when it sums several zlecenia
  focusLine(lineId: number): void {
    this.update(
      state => {
        const linesById = new Map(state.lines.map(line => [line.lineId, line]));
        const groupKey = OverseerViewModel.stageGroupKey(linesById.get(lineId)!, linesById);
        const groupLines = state.lines.filter(line => OverseerViewModel.stageGroupKey(line, linesById) === groupKey);

        return {
          ...state,
          // the row is listed already, or Wszystko lists it — every item under its parent has a row there
          workFilter: state.stageGroups.some(group => group.key === groupKey) ? state.workFilter : 'all',
          // a one-line group's row is the line; a multi-zlecenie group opens so the clicked one's line row is on screen
          expandedStageGroups: groupLines.length > 1 && !state.expandedStageGroups.includes(groupKey)
            ? [...state.expandedStageGroups, groupKey]
            : state.expandedStageGroups,
          focusedStageRow: { groupKey, lineId },
          // the open form belonged to the row it was opened on; the click is a navigation
          reportTarget: null
        };
      },
      deriveViews
    );
  }

  // The last dictionary entry is the finishing step by convention, so reporting into it
  // completes items by default; anywhere else the master ticks gotowe by hand
  startReport(target: ReportRow & Omit<ReportForm, 'completesItem' | 'openAt'> & { openAt?: Point }): void {
    this.update(state => ({
      ...state,
      reportTarget: {
        ...target,
        openAt: target.openAt ?? null,
        completesItem: target.operationTypeId == null || target.operationTypeId === lastOperationTypeId(state)
      }
    }));
  }

  toggleCompletesItem(): void {
    this.update(state => state.reportTarget
      ? { ...state, reportTarget: { ...state.reportTarget, completesItem: !state.reportTarget.completesItem } }
      : state);
  }

  cancelReport(): void {
    this.update(state => ({ ...state, reportTarget: null }));
  }

  confirmReport(rawAmount: string): Observable<void> {
    const target = this.state.reportTarget;

    if (!target) {
      return of(undefined);
    }

    const requested = parseAmount(rawAmount);

    if (!Number.isFinite(requested) || requested <= 0) {
      return of(undefined);
    }

    const targetLines = reportLines(this.state, target);

    const kind: ReportKind = { operationTypeId: target.operationTypeId, completesItem: target.completesItem };
    const allocations = WorkLine.allocate(targetLines, requested, kind, waitingByLineId(this.state, target));

    if (allocations.length === 0) {
      this.update(state => ({ ...state, reportTarget: null }));
      return of(undefined);
    }

    return this.provider.reportProgress(allocations, kind)
      .pipe(
        tap(response => {
          this.update(
            state => ({
              ...state,
              lines: WorkLine.applyAllocations(state.lines, allocations, 1, kind),
              reportTarget: null,
              lastReport: {
                reportId: response.reportId,
                bomItemId: targetLines[0].bomItemId,
                groupKey: stageGroupKey(state, allocations[0].lineId),
                message: buildReportMessage(state, allocations, requested, kind),
                action: 'report',
                allocations,
                kind
              }
            }),
            deriveViews
          );
        }),
        toVoid(),
        // A rejected report means this client is stale (someone else reported meanwhile) — refresh
        catchError(() => {
          this.update(state => ({ ...state, reportTarget: null }));
          return this.init();
        })
      );
  }

  // Nic do zrobienia (skips) or Przywróć for the lines the row named. A fact about how the
  // part is made, not an amount — the lines move on to their next stage, nothing is reported
  skipOperation(lineIds: number[], operationTypeId: number, skips: boolean): Observable<void> {
    if (lineIds.length === 0) {
      return of(undefined);
    }

    return this.provider.skipOperation(lineIds, operationTypeId, skips)
      .pipe(
        tap(response => {
          this.update(
            state => {
              const lines = WorkLine.applySkip(state.lines, lineIds, operationTypeId, skips);

              return {
                ...state,
                lines,
                reportTarget: null,
                lastReport: {
                  reportId: response.reportId,
                  bomItemId: lines.find(line => line.lineId === lineIds[0])!.bomItemId,
                  groupKey: stageGroupKey(state, lineIds[0]),
                  message: buildSkipMessage({ ...state, lines }, lineIds, operationTypeId, skips),
                  action: 'skip',
                  lineIds,
                  operationTypeId,
                  skips,
                  learnedLineIds: lineIds.filter(lineId => state
                    .lines
                    .find(line => line.lineId === lineId)!
                    .learnedSkippedOperationTypeIds
                    .includes(operationTypeId))
                }
              };
            },
            deriveViews
          );
        }),
        toVoid(),
        // Refused: someone reported at this stage meanwhile — refresh, as for a rejected report
        catchError(() => this.init())
      );
  }

  undoLastReport(): Observable<void> {
    const lastReport = this.state.lastReport;

    if (!lastReport) {
      return of(undefined);
    }

    return this.provider.undoReport(lastReport.reportId)
      .pipe(
        tap(() => {
          this.update(
            state => ({
              ...state,
              lines: lastReport.action === 'skip'
                ? WorkLine.applySkip(state.lines, lastReport.lineIds, lastReport.operationTypeId, !lastReport.skips, lastReport.learnedLineIds)
                : WorkLine.applyAllocations(state.lines, lastReport.allocations, -1, lastReport.kind),
              lastReport: null
            }),
            deriveViews
          );
        }),
        toVoid(),
        catchError(() => {
          this.update(state => ({ ...state, lastReport: null }));
          return this.init();
        })
      );
  }

  // One menu at a time: opening either closes the other
  openHeaderMenu(operationTypeId: number, position: Point): void {
    this.update(state => ({ ...state, headerMenu: { operationTypeId, position }, treeMenu: null }));
  }

  closeHeaderMenu(): void {
    this.update(state => ({ ...state, headerMenu: null }));
  }

  openTreeMenu(lineId: number, position: Point): void {
    this.update(state => ({ ...state, treeMenu: { lineId, position }, headerMenu: null }));
  }

  closeTreeMenu(): void {
    this.update(state => ({ ...state, treeMenu: null }));
  }

  // Only one header input is open at a time — opening another closes the first
  startHeaderEdit(headerEdit: HeaderEdit): void {
    this.update(state => ({ ...state, headerEdit, headerEditError: null, headerMenu: null }));
  }

  cancelHeaderEdit(): void {
    this.update(state => ({ ...state, headerEdit: null, headerEditError: null }));
  }

  // The header is the only place an operation name is ever typed — a visible,
  // plant-wide act, which is the slight friction 0024 wants against vocabulary entropy
  submitHeaderEdit(rawName: string): Observable<void> {
    const edit = this.state.headerEdit;
    const name = rawName.trim();

    if (!edit || !name) {
      return of(undefined);
    }

    const request: Observable<void> = edit.kind === 'insert'
      ? this.provider.createOperationType(name, edit.position).pipe(
          tap(created => this.update(
            state => ({ ...state, operationTypes: insertAt(state.operationTypes, edit.position, created) }),
            deriveViews
          )),
          toVoid())
      : this.provider.renameOperationType(edit.id, name).pipe(
          tap(() => this.update(state => ({
            ...state,
            operationTypes: state.operationTypes.map(type => type.id === edit.id ? { ...type, name } : type)
          }))));

    return request
      .pipe(
        tap(() => this.update(state => ({ ...state, headerEdit: null, headerEditError: null }))),
        catchError(error => {
          this.update(state => ({ ...state, headerEditError: apiMessage(error, 'Nie udało się zapisać etapu') }));
          return of(undefined);
        })
      );
  }

  // No confirmation: the menu offers delete only for a column with nothing attached, and
  // re-adding it is one action. A refusal means the column got used since load — refresh
  deleteOperation(id: number): Observable<void> {
    return this.provider.deleteOperationType(id)
      .pipe(
        tap(() => {
          this.update(
            state => ({
              ...state,
              operationTypes: renumber(state.operationTypes.filter(type => type.id !== id)),
              headerMenu: null
            }),
            deriveViews
          );
        }),
        toVoid(),
        catchError(error => this.provider.getOperationTypes().pipe(
          tap(operationTypes => this.update(
            state => ({
              ...state,
              operationTypes,
              headerMenu: null,
              headerEditError: apiMessage(error, 'Nie udało się usunąć etapu')
            }),
            deriveViews
          )),
          toVoid()
        ))
      );
  }
}

function insertAt(types: OperationType[], position: number, created: OperationType): OperationType[] {
  return renumber([...types.slice(0, position), created, ...types.slice(position)]);
}

// Mirrors the API's dense renumbering so column order never depends on a reload
function renumber(types: OperationType[]): OperationType[] {
  return types.map((type, position) => ({ ...type, position }));
}

function apiMessage(error: { error?: unknown }, fallback: string): string {
  return typeof error.error === 'string' ? error.error : fallback;
}

const deriveViews: UpdateFn<ProductionOverseerState> = state => {
  // Every view counts effective done (0117): a line skipped at every stage completes with its
  // parent, so a report or Cofnij on a frame moves its bolts at once, with no refetch
  const lines = OverseerViewModel.withEffectiveDone(state.lines, state.operationTypes);
  const readiness = OverseerViewModel.computeReadiness(lines, state.operationTypes);

  // "Do zrobienia" never lists a line that completes with its parent — there is nothing to do on it
  const itemGroups = OverseerViewModel.mapItemGroups(
    state.workFilter === 'ready'
      ? lines.filter(line => !OverseerViewModel.completesWithParent(line, state.operationTypes))
      : lines,
    readiness,
    state.operationTypes
  );

  const stage = OverseerViewModel.selectedOperationType(state.operationTypes, state.selectedOperationTypeId);
  const stageGroups = stage
    ? OverseerViewModel.mapStageGroups(lines, readiness, state.operationTypes, stage.id)
    : [];

  return {
    ...state,
    // "Do zrobienia" keeps only groups with something workable — plus the group of the
    // last report, so its confirmation and undo stay reachable after it goes done/hidden
    itemGroups: state.workFilter === 'ready'
      ? itemGroups.filter(group => group.workableNowTotal > 0 || group.bomItemId === state.lastReport?.bomItemId)
      : itemGroups,
    orderGroups: OverseerViewModel.mapOrderGroups(lines, readiness, state.operationTypes),
    // Same rule per stage: what waits there, plus the row of the last action at this stage
    stageGroups: state.workFilter === 'ready'
      ? stageGroups.filter(group => group.waitingTotal > 0 || isLastActionAt(state.lastReport, group))
      : stageGroups,
    stageCounts: OverseerViewModel.mapStageCounts(lines, readiness, state.operationTypes),
    contextTree: OverseerViewModel.mapContextTree(lines, readiness, state.operationTypes, stage?.id ?? null, state.focusedStageRow),
    summary: OverseerViewModel.mapSummary(lines)
  };
};

function isLastActionAt(lastReport: LastReport | null, group: StageGroupVm): boolean {
  if (lastReport?.groupKey !== group.key) {
    return false;
  }

  const operationTypeId = lastReport.action === 'skip' ? lastReport.operationTypeId : lastReport.kind.operationTypeId;

  return operationTypeId === group.operationTypeId;
}

function reportLines(state: ProductionOverseerState, target: ReportRow): WorkLine[] {
  switch (target.kind) {
    case 'item':
      return state.lines.filter(line => line.bomItemId === target.bomItemId);
    // A row's report never lands on a line that skips the stage (un-skipping is done by
    // reporting on that line, or with Przywróć) nor on a finished one — both have no room
    case 'stageGroup':
      return stageGroupLines(state, target.groupKey).filter(line => line.remainingAtStage > 0);
    case 'line':
      return state.lines.filter(line => line.lineId === target.lineId);
  }
}

// Only a Wg etapów row has a preference: fill what waits at the stage first
function waitingByLineId(state: ProductionOverseerState, target: ReportRow): Map<number, number> | undefined {
  return target.kind === 'stageGroup'
    ? new Map(stageGroupLines(state, target.groupKey).map(line => [line.lineId, line.waiting]))
    : undefined;
}

function stageGroupLines(state: ProductionOverseerState, groupKey: string): StageLineVm[] {
  return state.stageGroups.find(group => group.key === groupKey)?.lines ?? [];
}

function stageGroupKey(state: ProductionOverseerState, lineId: number): string {
  const linesById = new Map(state.lines.map(line => [line.lineId, line]));
  return OverseerViewModel.stageGroupKey(linesById.get(lineId)!, linesById);
}

function lastOperationTypeId(state: ProductionOverseerState): number | null {
  return state.operationTypes.at(-1)?.id ?? null;
}

function buildReportMessage(
  state: ProductionOverseerState,
  allocations: ProgressAllocation[],
  requested: number,
  kind: ReportKind
): string {
  const linesById = new Map(state.lines.map(line => [line.lineId, line]));
  const perOrder = groupBy(allocations, a => linesById.get(a.lineId)!.orderNumber);

  const parts = allocations.map(a => {
    const line = linesById.get(a.lineId)!;
    // Name the parent whenever the split lands inside one order
    const withParent = perOrder[line.orderNumber].length > 1 && line.parentName;
    const parent = withParent ? ` (${line.parentName})` : '';
    return `${formatNumber(a.delta)} → zam. ${line.orderNumber}${parent}`;
  });

  const total = normalizeAmount(allocations.reduce((acc, a) => acc + a.delta, 0));
  // a report lands on one item (or one part under a parent), so every allocated line shares the unit
  const unit = linesById.get(allocations[0].lineId)!.unit;
  const capped = total < requested ? ` (brakowało tylko ${formatNumber(total)} z ${formatNumber(requested)})` : '';
  const split = parts.length > 1 ? `rozdzielono: ${parts.join(', ')}` : parts[0];

  // The untagged report reads as before; a column report names its operation
  const operation = state.operationTypes.find(type => type.id === kind.operationTypeId);
  const column = operation ? ` · ${operation.name}${kind.completesItem ? ' · gotowe' : ''}` : '';

  return `Zapisano ${formatAmount(total, unit)}${capped}${column} — ${split}`;
}

// `Pręt stalowy (Rama brony) pomija etap Gięcie — teraz czeka na: Spawanie`
function buildSkipMessage(
  state: ProductionOverseerState,
  lineIds: number[],
  operationTypeId: number,
  skips: boolean
): string {
  const lines = state.lines.filter(line => lineIds.includes(line.lineId));
  const row = lines[0].parentName ? `${lines[0].itemName} (${lines[0].parentName})` : lines[0].itemName;
  const stageIndex = state.operationTypes.findIndex(type => type.id === operationTypeId);
  const stage = state.operationTypes[stageIndex].name;

  if (!skips) {
    return `${row} wraca na etap ${stage}`;
  }

  // The next stage each line still goes through — several when the lines differ
  const next = [...new Set(lines.flatMap(line => state
    .operationTypes
    .slice(stageIndex + 1)
    .find(type => !WorkLine.skips(line, type.id))?.name ?? []))];

  return `${row} pomija etap ${stage} — ${next.length > 0 ? `teraz czeka na: ${next.join(', ')}` : 'nie ma dalszych etapów'}`;
}
